/**
 * PhaserGame - Lazy Loading Module
 *
 * OPTIMIZATION: Phaser (~3MB) is now loaded lazily when user joins a room,
 * not on initial page load. This dramatically improves initial load time.
 *
 * The exported `phaserGame` is initially null and gets populated when
 * initializePhaserGame() is called (typically when joining a room).
 *
 * Components should check isPhaserGameReady() before accessing phaserGame.
 *
 * Phaser 4: stay on Phaser 3.x until v4 is stable and phaser-react-ui supports it;
 * see `config/phaserGameConfig.ts`.
 */

// Lazy-loaded Phaser game instance - initially null
let phaserGame: Phaser.Game | null = null
let initializationPromise: Promise<Phaser.Game> | null = null
let isInitializing = false

/**
 * Check if Phaser game is ready to use
 */
export function isPhaserGameReady(): boolean {
  return phaserGame !== null
}

/**
 * Check if Phaser is currently initializing
 */
export function isPhaserGameInitializing(): boolean {
  return isInitializing
}

/**
 * Get the Phaser game instance (may be null if not yet initialized)
 * For guaranteed access, use getPhaserGameAsync() instead.
 */
export function getPhaserGameSync(): Phaser.Game | null {
  return phaserGame
}

/**
 * Get the Phaser game instance, initializing it if needed.
 * This is the primary way to access Phaser when you need guaranteed access.
 */
export async function getPhaserGameAsync(): Promise<Phaser.Game> {
  if (phaserGame) {
    return phaserGame
  }
  
  if (initializationPromise) {
    return initializationPromise
  }
  
  return initializePhaserGame()
}

/**
 * Initialize Phaser game lazily. Called when user joins a room.
 * Returns immediately if already initialized.
 */
export async function initializePhaserGame(): Promise<Phaser.Game> {
  // Return existing instance
  if (phaserGame) {
    return phaserGame
  }
  
  // Return existing promise if initialization in progress
  if (initializationPromise) {
    return initializationPromise
  }
  
  console.log('[PhaserGame] Starting lazy initialization of Phaser...')
  const startTime = performance.now()
  isInitializing = true
  
  initializationPromise = (async () => {
    try {
      // Dynamically import Phaser (this is the ~3MB load)
      // Use namespace import as Phaser doesn't have a default export in ESM
      const PhaserModule = await import('phaser')
      const Phaser = (PhaserModule as any).default || PhaserModule
      console.log(`[PhaserGame] Phaser module loaded in ${(performance.now() - startTime).toFixed(0)}ms`)
      
      // Dynamically import scenes
      const sceneStartTime = performance.now()
      const [BootstrapModule, BackgroundModule, GameModule] = await Promise.all([
        import('./scenes/Bootstrap'),
        import('./scenes/Background'),
        import('./scenes/Game')
      ])
      console.log(`[PhaserGame] Scenes loaded in ${(performance.now() - sceneStartTime).toFixed(0)}ms`)

      const Bootstrap = BootstrapModule.default
      const Background = BackgroundModule.default
      const Game = GameModule.default

      const {
        buildPhaserArcadePhysicsConfig,
        buildPhaserFpsConfig,
        buildPhaserRenderConfig,
        resolvePhaserRendererType,
      } = await import('./config/phaserGameConfig')

      const config: Phaser.Types.Core.GameConfig = {
        type: resolvePhaserRendererType(Phaser),
        parent: 'phaser-container',
        backgroundColor: '#93cbee',
        pixelArt: true,
        roundPixels: true,
        antialias: false,
        scale: {
          mode: Phaser.Scale.ScaleModes.RESIZE,
          width: window.innerWidth,
          height: window.innerHeight,
        },
        render: buildPhaserRenderConfig(),
        physics: {
          default: 'arcade',
          arcade: buildPhaserArcadePhysicsConfig(),
        },
        fps: buildPhaserFpsConfig(),
        autoFocus: true,
        scene: [Bootstrap, Background, Game],
      }

      phaserGame = new Phaser.Game(config)

      // Prevent Phaser from pausing the Web Audio context when the window loses focus.
      // This stops background tabs from silencing WebRTC (LiveKit) audio/microphone.
      if (phaserGame.sound) {
        phaserGame.sound.pauseOnBlur = false
      }
      
      // Store in global scope for backward compatibility
      ;(window as any).game = phaserGame
      ;(window as any).phaserGame = phaserGame

      const totalTime = performance.now() - startTime
      console.log(`[PhaserGame] Phaser game initialized in ${totalTime.toFixed(0)}ms total`)
      
      return phaserGame
    } catch (error) {
      console.error('[PhaserGame] Failed to initialize Phaser:', error)
      initializationPromise = null
      throw error
    } finally {
      isInitializing = false
    }
  })()
  
  return initializationPromise
}

/**
 * Preload Phaser in the background.
 * Call this when user is likely to join a room soon (e.g., viewing room list).
 */
export function preloadPhaserGame(): void {
  if (!phaserGame && !initializationPromise) {
    console.log('[PhaserGame] Preloading Phaser in background...')
    initializePhaserGame().catch((error) => {
      console.warn('[PhaserGame] Preload failed:', error)
    })
  }
}

/**
 * Wait for Phaser to be ready (for use in components)
 */
export function waitForPhaserReady(): Promise<Phaser.Game> {
  return getPhaserGameAsync()
}

// Default export - initially null, populated after initializePhaserGame()
// Components importing this should handle null case or use isPhaserGameReady()
export default phaserGame as Phaser.Game

// Re-export for compatibility with existing imports
export { phaserGame }
